import { SPECIALIST_HOSTNAME } from "env-vars";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { storePatientToken } from "helpers";

interface FetchWithAuthOptions extends RequestInit {
  skipTokenRefresh?: boolean;
}

interface AppMetadata {
  ciq_id?: string;
  location_id?: string;
}

interface TokenPayload extends JwtPayload {
  email?: string;
  app_metadata?: AppMetadata;
}

/**
 * Fetch wrapper that handles authentication and token refresh
 * for passwordless authentication flow
 */
export async function fetchWithAuth(
  url: string,
  options: FetchWithAuthOptions = {}
): Promise<Response> {
  const { skipTokenRefresh, ...fetchOptions } = options;
  const idToken = localStorage.getItem("id_token");

  const response = await fetch(url, {
    ...fetchOptions,
    headers: {
      ...fetchOptions.headers,
      Authorization: `Bearer ${idToken}`,
    },
  });

  // If 401 and using passwordless auth, try to refresh token once
  if (response.status === 401 && !skipTokenRefresh) {
    const authMethod = sessionStorage.getItem("auth_method");

    if (authMethod === "passwordless") {
      const refreshed = await refreshPasswordlessToken();

      if (refreshed) {
        // Retry the request with new token
        const newToken = localStorage.getItem("id_token");
        return fetch(url, {
          ...fetchOptions,
          headers: {
            ...fetchOptions.headers,
            Authorization: `Bearer ${newToken}`,
          },
        });
      } else {
        // Refresh failed, redirect to verification page with cached token
        const paToken = sessionStorage.getItem("pa_token");
        if (paToken) {
          window.location.href = `/login/verify?pa=${paToken}`;
        } else {
          window.location.href = "/login/passwordless";
        }
      }
    }
  }

  return response;
}

/**
 * Helper function for token refresh
 */
async function refreshPasswordlessToken(): Promise<boolean> {
  const paToken = sessionStorage.getItem("pa_token");
  const paDob = sessionStorage.getItem("pa_dob");
  const survey_kind = sessionStorage.getItem("kind");

  if (!paToken || !paDob) {
    return false;
  }

  try {
    const response = await fetch(
      `${SPECIALIST_HOSTNAME}/api/v1/authentication/verify_magic_link`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pa: paToken,
          date_of_birth: paDob,
          survey_kind: survey_kind,
        }),
      }
    );

    if (response.ok) {
      const data = await response.json();
      const accessToken = data.access_token;
      storePatientToken(accessToken, data.expires_in);

      // Decode JWT to extract patient identifiers from app_metadata
      try {
        const decoded = jwtDecode<TokenPayload>(accessToken);
        const appMetadata = decoded.app_metadata;

        // Update stored identifiers from token
        if (appMetadata?.ciq_id) {
          localStorage.setItem("ciq_id", appMetadata.ciq_id);
        }
        if (appMetadata?.location_id) {
          localStorage.setItem("location_id", appMetadata.location_id);
        }
        if (decoded.email) {
          localStorage.setItem("ciq_email", decoded.email);
        }
      } catch (decodeError) {
        console.error("Error decoding refreshed token:", decodeError);
      }

      return true;
    }

    return false;
  } catch (err) {
    console.error("Token refresh failed:", err);
    return false;
  }
}
